import React, { Children, useState } from 'react'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import {
  Box,
  Button,
  Collapse,
  Divider,
  Heading,
  StackView,
  Text,
  Tooltip,
} from '@planning-center/ui-kit'

import { markdownToReact } from '../markdown-to-react'

function getTypeShape(type) {
  const rawLines = type.raw.split('\n').filter((line) => line !== '')
  const shape = type.signature
    ? type.signature.properties.reduce(
        (value, property) => ({
          ...value,
          [property.key]: property.value,
        }),
        {}
      )
    : type.value
  let temporaryComment = []
  let parsingComment = false
  rawLines.forEach((line, lineIndex) => {
    const startOfComment = line.includes('/*')
    const endOfComment = line.includes('*/')
    if (startOfComment) {
      parsingComment = true
    }
    if (endOfComment) {
      const nextLine = rawLines[lineIndex + 1]
      const name = nextLine.slice(0, nextLine.search('\\?|:')).trimStart()
      if (!shape[name].description) {
        shape[name].description = startOfComment
          ? line.split('/**')[1].split('*/')[0]
          : temporaryComment.join('\n')
      }
      temporaryComment = []
      parsingComment = false
    }
    if (!startOfComment && !endOfComment && parsingComment) {
      const parsedLine = line.split('*')[1].trimStart()
      temporaryComment.push(parsedLine)
    }
  })
  return shape
}

function getTypeName(type) {
  if (type) {
    switch (type.name) {
      case 'arrayOf':
        return `array<${getTypeName(type.value)}>`
      case 'bool':
        return 'boolean'
      case 'enum':
        return Array.isArray(type.value)
          ? type.value
              .map((value) => value.value.split("'").join(''))
              .join(' | ')
          : type.value
      case 'func':
        return 'function'
      case 'literal':
        return type.value
      case 'signature':
        return type.type === 'function' ? type.raw : type.name
      case 'union':
        return (type.elements || type.value)
          .filter(({ name }) => name !== '')
          .map(getTypeName)
          .join(' | ')
      default:
        return type.name
    }
  } else {
    return null
  }
}

function getShapeComponent(type) {
  if (!type) {
    return null
  }
  switch (type.name) {
    case 'arrayOf':
      return getShapeComponent(type.value)
    case 'shape':
    case 'signature':
      return type.type === 'function' ? null : (
        <ShapeProps shape={getTypeShape(type)} />
      )
    case 'Array':
    case 'union':
      const components = (type.elements || type.value)
        .map(getShapeComponent)
        .filter(Boolean)
      return components.length > 0 ? Children.toArray(components) : null
    default:
      return null
  }
}

function ShapeProps({ shape }) {
  return (
    <StackView
      padding={3}
      marginTop={1.5}
      spacing={3}
      radius={3}
      backgroundColor="surface"
    >
      {Object.keys(shape).reduce((collection, key) => {
        const type = shape[key]
        collection.push(
          <StackView key={key} spacing={1}>
            <StackView axis="horizontal" spacing={0.5}>
              <Text as="span" color="foregroundSecondary" weight={600}>
                {key}:
              </Text>
              <Text as="span" color="foregroundTertiary">
                {getTypeName(type)}
              </Text>
            </StackView>
            {type.description && markdownToReact(type.description)}
          </StackView>
        )
        return collection
      }, [])}
    </StackView>
  )
}

function Description({ description }) {
  return (
    <Box lineHeight={4} marginTop={1.5}>
      <MDXRenderer>{description.childMdx.body}</MDXRenderer>
    </Box>
  )
}

function PropDoc({ name, description, type, flowType, tsType, doclets }) {
  const [open, setOpen] = useState(false)
  const parsedType = flowType || tsType || type
  const typeBadge = getTypeName(parsedType)
  const shapeComponent = getShapeComponent(parsedType)
  const deprecated = doclets.find((doclet) => doclet.tag === 'deprecated')
  return (
    <Box id={name}>
      <StackView axis="horizontal" alignment="center">
        <Heading
          as="a"
          href={`#${name}`}
          level={3}
          shrink={0}
          weight={600}
          color="foregroundSecondary"
          hover={{ cursor: 'pointer' }}
        >
          {name}
        </Heading>
        {typeBadge && (
          <StackView
            as="code"
            axis="horizontal"
            alignment="center"
            shrink={1}
            paddingHorizontal={0.5}
            paddingVertical={0.25}
            marginLeft={1}
            radius={5}
            backgroundColor="backgroundTertiary"
            color="foreground"
            position="relative"
          >
            {typeBadge}
          </StackView>
        )}
        {shapeComponent && (
          <Button
            theme={false}
            size="sm"
            title={`${open ? 'close' : 'open'} ${name} props`}
            icon={{ name: 'caret-down' }}
            transform={`rotate(${open ? 0 : -90}deg)`}
            transition="all 240ms ease"
            color="foregroundSecondary"
            onClick={() => setOpen(!open)}
          />
        )}
        {deprecated && (
          <Tooltip title={deprecated.value}>
            <StackView
              axis="horizontal"
              alignment="center"
              spacing={0.5}
              marginLeft={1}
            >
              <StackView
                width={1}
                height={1}
                radius="circle"
                backgroundColor="error-light"
              />
              <Text size={4} weight={600} color="error" cursor="default">
                Deprecated
              </Text>
            </StackView>
          </Tooltip>
        )}
      </StackView>

      {shapeComponent && <Collapse open={open}>{shapeComponent}</Collapse>}

      {description && <Description description={description} />}
    </Box>
  )
}

function PropsTable({ props }) {
  return (
    <StackView
      padding={3}
      marginHorizontal={-3}
      spacing={<Divider margin={2} />}
    >
      <Heading id="props" level={2}>
        Props
      </Heading>
      {props.map((prop) => (
        <PropDoc key={prop.name} {...prop} />
      ))}
    </StackView>
  )
}

export default PropsTable
